
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  RadarChart, 
  PolarGrid, 
  PolarAngleAxis,
  PolarRadiusAxis, 
  Radar, 
  Legend,
  ResponsiveContainer,
  Tooltip
} from 'recharts';
import { Badge } from '@/components/ui/badge';

interface Skill {
  name: string;
  proficiency_level: string;
  years_of_experience: number;
  skill_name?: string; // Added for compatibility
}

interface SkillsSpiderChartProps {
  skills: Skill[];
}

const getProficiencyValue = (level: string): number => {
  switch(level?.toLowerCase()) {
    case 'beginner': return 25;
    case 'intermediate': return 50;
    case 'advanced': return 75;
    case 'expert': return 100;
    default: return 0;
  }
};

const getProficiencyLabel = (value: number): string => {
  if (value <= 25) return 'Beginner';
  if (value <= 50) return 'Intermediate';
  if (value <= 75) return 'Advanced';
  return 'Expert';
};

const CustomTooltip = ({ active, payload }: any) => {
  if (active && payload && payload.length) {
    const skill = payload[0].payload.skill;
    const proficiency = payload[0].value;
    const yearsExp = payload[0].payload.years || 0;
    
    return (
      <div className="bg-white p-2 border border-gray-200 shadow-sm rounded-md">
        <p className="font-medium text-gray-800">{skill}</p>
        <p className="text-sm text-gray-600">Proficiency: <span className="font-medium">{getProficiencyLabel(proficiency)}</span> ({proficiency}%)</p>
        <p className="text-sm text-gray-600">Experience: {yearsExp} year{yearsExp !== 1 ? 's' : ''}</p>
      </div>
    );
  }
  return null;
};

const SkillsSpiderChart: React.FC<SkillsSpiderChartProps> = ({ skills }) => {
  // Normalize skill data to ensure name is available
  const normalizedSkills = skills.map(skill => ({
    ...skill,
    name: skill.skill_name || skill.name
  }));
  
  // Only show if we have skills
  if (normalizedSkills.length === 0) {
    return null;
  }
  
  // Only show top 6 skills (based on proficiency and years of experience)
  const topSkills = [...normalizedSkills]
    .sort((a, b) => {
      const aProficiency = getProficiencyValue(a.proficiency_level);
      const bProficiency = getProficiencyValue(b.proficiency_level);
      
      // First compare by proficiency
      if (bProficiency !== aProficiency) {
        return bProficiency - aProficiency;
      }
      
      // If proficiency is the same, compare by years of experience
      return (b.years_of_experience || 0) - (a.years_of_experience || 0);
    })
    .slice(0, 6);

  const chartData = topSkills.map((skill) => ({
    skill: skill.name,
    value: getProficiencyValue(skill.proficiency_level),
    years: skill.years_of_experience || 0,
    fullMark: 100
  }));

  // If less than 3 skills, the chart doesn't look good
  if (normalizedSkills.length < 3) {
    return (
      <Card className="border border-[#0EA5E9]/20 shadow-md bg-white/80 h-full">
        <CardHeader>
          <CardTitle className="text-xl text-gray-800">Skills Overview</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col items-center justify-center">
            <p className="text-gray-500 text-center mb-4">
              Add at least 3 skills in your profile to see your skills visualization.
            </p>
            {normalizedSkills.length > 0 && (
              <div className="flex flex-wrap gap-2 justify-center">
                {normalizedSkills.map((skill, index) => (
                  <Badge key={index} className="bg-[#0EA5E9] hover:bg-[#0EA5E9]/80">
                    {skill.name} • {skill.proficiency_level} • {skill.years_of_experience || 0} yr{(skill.years_of_experience !== 1) ? 's' : ''}
                  </Badge>
                ))}
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border border-[#0EA5E9]/20 shadow-md bg-white/80 h-full">
      <CardHeader>
        <CardTitle className="text-xl text-gray-800">Skills Overview</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-64 w-full mb-4">
          <ResponsiveContainer width="100%" height="100%">
            <RadarChart outerRadius="80%" data={chartData}>
              <PolarGrid />
              <PolarAngleAxis dataKey="skill" />
              <PolarRadiusAxis domain={[0, 100]} tickFormatter={(value) => getProficiencyLabel(value)} />
              <Tooltip content={<CustomTooltip />} />
              <Radar
                name="Proficiency"
                dataKey="value"
                stroke="#0EA5E9"
                fill="#0EA5E9"
                fillOpacity={0.6}
              />
              <Legend />
            </RadarChart>
          </ResponsiveContainer>
        </div>
        
        <div className="flex flex-wrap gap-2 justify-center mt-4">
          {normalizedSkills.map((skill, index) => (
            <Badge key={index} className="bg-[#0EA5E9] hover:bg-[#0EA5E9]/80">
              {skill.name} • {skill.proficiency_level} • {skill.years_of_experience || 0} yr{(skill.years_of_experience !== 1) ? 's' : ''}
            </Badge>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default SkillsSpiderChart;
